/**
 * Firebase Service
 * Initializes Firebase and exports auth and firestore instances.
 *
 * App Check: when VITE_APPCHECK_SITE_KEY is set, attaches a
 * ReCAPTCHA-Enterprise-backed App Check token to every Firestore / Functions
 * call. Enforcement on the server side is configured separately in the
 * Firebase console (start in Monitor mode, flip to Enforce once traffic
 * looks clean — see SECURITY.md). If the env var is missing the init
 * silently no-ops so local dev / CI doesn't break.
 */

import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { initializeFirestore } from 'firebase/firestore';
import { getFunctions } from 'firebase/functions';
import { getStorage } from 'firebase/storage';
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from 'firebase/app-check';
import firebaseConfig from '../config/firebase';

// Initialize Firebase with error handling for placeholder config
let app, auth, db, functions, storage;

try {
  // Check if we have valid Firebase config
  const hasValidConfig = firebaseConfig.apiKey &&
                         !firebaseConfig.apiKey.includes('your-') &&
                         firebaseConfig.projectId &&
                         !firebaseConfig.projectId.includes('your-');

  if (hasValidConfig) {
    app = initializeApp(firebaseConfig);

    // App Check — attach a verifiable token to every Firestore/Functions call
    // so server-side enforcement can reject requests that don't come from
    // this app. Missing key = local dev or pre-provisioning; skip silently.
    const appCheckKey = import.meta.env.VITE_APPCHECK_SITE_KEY;
    if (appCheckKey && !appCheckKey.includes('your-')) {
      try {
        initializeAppCheck(app, {
          provider: new ReCaptchaEnterpriseProvider(appCheckKey),
          isTokenAutoRefreshEnabled: true,
        });
      } catch (appCheckErr) {
        // App Check init failures must not break the app — we observe
        // failures via the Firebase console / logs instead. Common cause:
        // domain not added to allowed list in reCAPTCHA Enterprise.
        console.warn('App Check init failed (non-fatal):', appCheckErr?.message);
      }
    }

    auth = getAuth(app);
    // Use the Hong Kong database (asia-east2) for lower latency in Philippines
    db = initializeFirestore(app, {}, 'hongkong');
    // Use us-central1 region for functions (where they're deployed)
    functions = getFunctions(app, 'us-central1');
    // Initialize Firebase Storage for file uploads (logos, etc.)
    storage = getStorage(app);
  } else {
    console.warn('⚠️ Firebase not configured. Using placeholder values.');
    console.warn('To enable full functionality, update your .env file with real Firebase credentials.');
    console.warn('See HOW_TO_RUN.md for instructions.');

    // Create mock instances to prevent errors
    app = null;
    auth = null;
    db = null;
    functions = null;
    storage = null;
  }
} catch (error) {
  console.error('Firebase initialization error:', error);
  console.warn('Running in demo mode without Firebase.');
  app = null;
  auth = null;
  db = null;
  functions = null;
  storage = null;
}

export { auth, db, functions, storage };
export default app;
